/**
 * Color-by legend entries for Scatter History (aligned with scatterColors.ts).
 */
import {
  buildDayColorMapForScatter,
  buildSourceColorMapForScatter,
  getScatterDayKey,
} from './scatterColors';
import {
  buildExploreCategoryColorMap,
  getExploreRowGroupKey,
  groupKeyToLegendLabel,
} from './exploreHistoryColorGroups';
import {
  resolveScatterPointDatetimeIso,
  resolveScatterPointEventId,
} from './scatterLassoSelection';
import { defaultChannelsStore } from '../store/defaultChannelsStore';

export interface ScatterColorLegendItem {
  label: string;
  color: string;
}

export interface ScatterLegendHighlightState {
  colorType: string;
  label: string;
}

const FIXED_LEGEND_BY_COLOR_TYPE: Record<string, ScatterColorLegendItem[]> = {
  GRADE: [
    { label: 'Grade 0', color: 'lightgray' },
    { label: 'Grade 1', color: 'red' },
    { label: 'Grade 2', color: 'lightgreen' },
    { label: 'Grade 3', color: 'darkgreen' },
  ],
  TACK: [
    { label: 'Starboard', color: 'green' },
    { label: 'Port', color: 'red' },
  ],
  'UW/DW': [
    { label: 'Upwind (<75°)', color: 'blue' },
    { label: 'Reaching (75–120°)', color: 'orange' },
    { label: 'Downwind (>120°)', color: 'purple' },
  ],
};

export function allScatterRows(charts: unknown[]): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  if (!Array.isArray(charts)) {return rows;}
  for (const chart of charts) {
    if (!chart || typeof chart !== 'object') {continue;}
    const seriesList = (chart as { series?: unknown[] }).series;
    if (!Array.isArray(seriesList)) {continue;}
    for (const series of seriesList) {
      if (!series || typeof series !== 'object') {continue;}
      const data =
        (series as { originalData?: unknown[]; data?: unknown[] }).originalData ??
        (series as { data?: unknown[] }).data ??
        [];
      if (Array.isArray(data)) {
        for (const row of data) {
          if (row && typeof row === 'object') {
            rows.push(row as Record<string, unknown>);
          }
        }
      }
    }
  }
  return rows;
}

function chartLegendLabel(chart: Record<string, unknown>, index: number): string {
  const series = Array.isArray(chart.series) ? chart.series[0] : null;
  if (series && typeof series === 'object') {
    const yName = (series as { yaxis?: { name?: string } }).yaxis?.name;
    const xName = (series as { xaxis?: { name?: string } }).xaxis?.name;
    if (yName && xName) {return `${yName} vs ${xName}`;}
    if (yName) {return String(yName);}
    if (xName) {return String(xName);}
  }
  const title = chart.title ?? chart.name;
  if (title != null && String(title).trim()) {return String(title);}
  return `Chart ${index + 1}`;
}

function buildDefaultLegendItems(charts: unknown[]): ScatterColorLegendItem[] {
  if (!Array.isArray(charts) || charts.length === 0) {return [];}
  const items: ScatterColorLegendItem[] = [];
  const seen = new Set<string>();

  charts.forEach((chart, index) => {
    if (!chart || typeof chart !== 'object') {return;}
    const c = chart as Record<string, unknown>;
    const series = Array.isArray(c.series) ? c.series[0] : null;
    if (series && typeof series === 'object') {
      const s = series as { colorType?: string };
      if (s.colorType === 'By Channel') {return;}
    }
    let color = '#1f77b4';
    if (series && typeof series === 'object') {
      const s = series as { color?: string };
      if (s.color) {color = String(s.color);}
    }
    const label = chartLegendLabel(c, index);
    const key = `${label}|${color}`;
    if (seen.has(key)) {return;}
    seen.add(key);
    items.push({ label, color });
  });

  return items;
}

export function buildScatterHistoryColorLegendItems(
  charts: unknown[],
  colorType: string
): ScatterColorLegendItem[] {
  const ct = colorType || 'DEFAULT';

  if (ct === 'DEFAULT') {
    return buildDefaultLegendItems(charts);
  }

  const fixed = FIXED_LEGEND_BY_COLOR_TYPE[ct];
  if (fixed) {
    return fixed;
  }

  const rows = allScatterRows(charts);
  if (rows.length === 0) {return [];}

  if (ct === 'SOURCE') {
    const map = buildSourceColorMapForScatter(rows);
    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([label, color]) => ({ label, color }));
  }

  if (ct === 'DAY') {
    const map = buildDayColorMapForScatter(rows);
    const days = [...new Set(rows.map(getScatterDayKey))].sort();
    return days.map((day) => ({
      label: day,
      color: map[day] ?? '#888',
    }));
  }

  if (ct === 'EVENT' || ct === 'YEAR' || ct === 'CONFIG' || ct === 'CREW') {
    const map = buildExploreCategoryColorMap(rows, ct);
    return Object.keys(map)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((key) => ({
        label: groupKeyToLegendLabel(ct, key),
        color: map[key] ?? '#888',
      }));
  }

  return [];
}

export function getChartLegendLabelForScatterHistory(chart: unknown, index: number): string {
  if (!chart || typeof chart !== 'object') {return `Chart ${index + 1}`;}
  return chartLegendLabel(chart as Record<string, unknown>, index);
}

function twaFromRow(row: Record<string, unknown>, twaChannelName: string): number {
  const raw = row[twaChannelName] ?? row.Twa ?? row.twa ?? row.TWA;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function gradeFromRow(row: Record<string, unknown>): number | null {
  const raw = row.grade ?? row.Grade ?? row.GRADE;
  if (raw == null) {return null;}
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function scatterRowMatchesLegendItem(
  row: Record<string, unknown>,
  colorType: string,
  label: string,
  twaChannelName: string,
  chartLegendLabelForChart?: string | null
): boolean {
  const ct = colorType || 'DEFAULT';

  if (ct === 'DEFAULT') {
    if (!chartLegendLabelForChart) {return false;}
    return chartLegendLabelForChart === label;
  }

  if (ct === 'GRADE') {
    const match = /^Grade\s+(\d+)$/.exec(label);
    if (!match) {return false;}
    const target = Number(match[1]);
    const g = gradeFromRow(row);
    return g != null && g === target;
  }

  if (ct === 'TACK') {
    const twa = twaFromRow(row, twaChannelName);
    if (label === 'Starboard') {return twa > 0;}
    if (label === 'Port') {return twa <= 0;}
    return false;
  }

  if (ct === 'UW/DW') {
    const absTwa = Math.abs(twaFromRow(row, twaChannelName));
    if (label.startsWith('Upwind')) {return absTwa < 75;}
    if (label.startsWith('Reaching')) {return absTwa >= 75 && absTwa <= 120;}
    if (label.startsWith('Downwind')) {return absTwa > 120;}
    return false;
  }

  if (ct === 'SOURCE') {
    const name = String(row.source_name ?? row.SOURCE_NAME ?? '').trim() || 'unknown';
    return name.localeCompare(label, undefined, { sensitivity: 'base' }) === 0;
  }

  if (ct === 'DAY') {
    return getScatterDayKey(row) === label;
  }

  if (ct === 'EVENT' || ct === 'YEAR' || ct === 'CONFIG' || ct === 'CREW') {
    const key = getExploreRowGroupKey(row, ct, twaChannelName);
    return groupKeyToLegendLabel(ct, key) === label || key === label;
  }

  return false;
}

export function rowsMatchingLegendItem(
  rows: Record<string, unknown>[],
  colorType: string,
  label: string,
  twaChannelName?: string
): Record<string, unknown>[] {
  const twa = twaChannelName ?? defaultChannelsStore.twaName();
  return rows.filter((row) => scatterRowMatchesLegendItem(row, colorType, label, twa));
}

export function collectScatterPointIdsFromRows(rows: Record<string, unknown>[]): Set<number> {
  const ids = new Set<number>();
  for (const row of rows) {
    const raw = row.ID ?? row.id;
    const n = Number(raw);
    if (Number.isFinite(n)) {
      ids.add(n);
    }
  }
  return ids;
}

function seriesRowsFromChart(chart: unknown): Record<string, unknown>[] {
  if (!chart || typeof chart !== "object") {return [];}
  const seriesList = (chart as { series?: unknown[] }).series;
  if (!Array.isArray(seriesList)) {return [];}
  const rows: Record<string, unknown>[] = [];
  for (const series of seriesList) {
    if (!series || typeof series !== "object") {continue;}
    const data =
      (series as { originalData?: unknown[]; data?: unknown[] }).originalData ??
      (series as { data?: unknown[] }).data ??
      [];
    if (!Array.isArray(data)) {continue;}
    for (const row of data) {
      if (row && typeof row === "object") {
        rows.push(row as Record<string, unknown>);
      }
    }
  }
  return rows;
}

/**
 * Point IDs for a color-legend click. DEFAULT entries are chart titles, not row
 * fields — `rowsMatchingLegendItem` cannot match those without a per-chart label.
 */
export function collectScatterPointIdsForLegendClick(
  charts: unknown[],
  colorType: string,
  label: string,
  twaChannelName?: string
): Set<number> {
  const ct = colorType || "DEFAULT";
  if (!Array.isArray(charts)) {return new Set();}

  if (ct === "DEFAULT") {
    const ids = new Set<number>();
    charts.forEach((chart, index) => {
      if (getChartLegendLabelForScatterHistory(chart, index) !== label) {return;}
      for (const id of collectScatterPointIdsFromRows(seriesRowsFromChart(chart))) {
        ids.add(id);
      }
    });
    return ids;
  }

  return collectScatterPointIdsFromRows(
    rowsMatchingLegendItem(allScatterRows(charts), ct, label, twaChannelName)
  );
}

/** Add ids to current, or remove them if every id is already selected. */
export function toggleIdsInSet(current: Set<number>, ids: Set<number>): Set<number> {
  if (ids.size === 0) {
    return current;
  }
  const allPresent = [...ids].every((id) => current.has(id));
  if (allPresent) {
    return new Set([...current].filter((id) => !ids.has(id)));
  }
  return new Set([...current, ...ids]);
}

export function collectEventIdsFromLegendRows(rows: Record<string, unknown>[]): number[] {
  const ids = new Set<number>();
  for (const row of rows) {
    const id = resolveScatterPointEventId(row);
    if (id != null) {ids.add(id);}
  }
  return [...ids];
}

export function collectDatetimeRangesFromLegendRows(
  rows: Record<string, unknown>[]
): Array<{ start_time: string; end_time: string }> {
  const ranges: Array<{ start_time: string; end_time: string }> = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const iso = resolveScatterPointDatetimeIso(row);
    if (!iso || seen.has(iso)) {continue;}
    seen.add(iso);
    ranges.push({ start_time: iso, end_time: iso });
  }
  return ranges;
}

export function isScatterPointHighlighted(
  row: Record<string, unknown>,
  opts: {
    cutActive: boolean;
    selectedEvents: number[];
    selectedRanges: Array<{ start_time?: string; end_time?: string }>;
    legendHighlight: ScatterLegendHighlightState | null;
    twaChannelName: string;
    chartLegendLabel?: string | null;
  }
): boolean {
  if (opts.cutActive) {return true;}

  const hasEventSelection = opts.selectedEvents.length > 0;
  const hasRangeSelection = opts.selectedRanges.length > 0;
  const hasLegendHighlight = opts.legendHighlight != null;
  const hasAnyHighlight = hasEventSelection || hasRangeSelection || hasLegendHighlight;

  if (!hasAnyHighlight) {return true;}

  if (hasEventSelection) {
    const eid = resolveScatterPointEventId(row);
    if (eid != null && opts.selectedEvents.includes(eid)) {return true;}
  }

  if (hasRangeSelection) {
    const iso = resolveScatterPointDatetimeIso(row);
    if (iso) {
      const ts = new Date(iso).getTime();
      if (
        opts.selectedRanges.some((range) => {
          if (!range.start_time || !range.end_time) {return false;}
          return (
            ts >= new Date(range.start_time).getTime() &&
            ts <= new Date(range.end_time).getTime()
          );
        })
      ) {
        return true;
      }
    }
  }

  if (hasLegendHighlight && opts.legendHighlight) {
    return scatterRowMatchesLegendItem(
      row,
      opts.legendHighlight.colorType,
      opts.legendHighlight.label,
      opts.twaChannelName,
      opts.chartLegendLabel ?? null
    );
  }

  return false;
}

export function scatterHistoryColorByDisplayName(colorType: string): string {
  switch (colorType) {
    case 'GRADE':
      return 'Grade';
    case 'TACK':
      return 'Tack';
    case 'UW/DW':
      return 'UW/DW';
    case 'SOURCE':
      return 'Source';
    case 'DAY':
      return 'Day';
    default:
      return 'Default';
  }
}
